import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { OrganizationConfigProvider } from "@/context/OrganizationConfigContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ModuleGate from "@/components/ModuleGate";
import MobileShell from "@/components/layouts/MobileShell";
import DesktopShell from "@/components/layouts/DesktopShell";
import PlatformShell from "@/components/layouts/PlatformShell";
import ErrorBoundary from "@/components/ErrorBoundary";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ResetPassword from "@/pages/ResetPassword";
import Unauthorized from "@/pages/Unauthorized";
import RoleRedirect from "@/pages/RoleRedirect";
import ParentDashboard from "@/pages/parent/Dashboard";
import ParentFamily from "@/pages/parent/Family";
import ParentActivity from "@/pages/parent/Activity";
import ParentActivityDetail from "@/pages/parent/ActivityDetail";
import TeacherDashboard from "@/pages/teacher/Dashboard";
import TeacherRoster from "@/pages/teacher/Roster";
import TeacherAttendance from "@/pages/teacher/Attendance";
import TeacherReportEditor from "@/pages/teacher/ReportEditor";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminDailyReports from "@/pages/admin/DailyReports";
import AdminIncidents from "@/pages/admin/Incidents";
import AdminEnrollment from "@/pages/admin/Enrollment";
import AdminEnrollmentDetail from "@/pages/admin/EnrollmentDetail";
import AdminAdmissions from "@/pages/admin/Admissions";
import CmsDashboard from "@/pages/cms/Dashboard";
import CmsPages from "@/pages/cms/Pages";
import CmsPageEditor from "@/pages/cms/PageEditor";
import CmsAnnouncements from "@/pages/cms/Announcements";
import CmsMedia from "@/pages/cms/Media";
import MessagingInbox from "@/pages/messaging/Inbox";
import MessagingThread from "@/pages/messaging/Thread";
import IncidentsList from "@/pages/incidents/IncidentsList";
import IncidentDetail from "@/pages/incidents/IncidentDetail";
import TeacherIncidentNew from "@/pages/teacher/IncidentNew";
import TeacherPhotos from "@/pages/teacher/Photos";
import TeacherTimesheet from "@/pages/teacher/Timesheet";
import TeacherSchedule from "@/pages/teacher/Schedule";
import TeacherRatioCapacity from "@/pages/teacher/RatioCapacity";
import TeacherFoodProgram from "@/pages/teacher/FoodProgram";
import ParentEnrollment from "@/pages/parent/Enrollment";
import ParentEnrollmentForm from "@/pages/parent/EnrollmentForm";
import ParentEnrollmentStatus from "@/pages/parent/EnrollmentStatus";
import ParentBilling from "@/pages/parent/Billing";
import ParentBillingSuccess from "@/pages/parent/BillingSuccess";
import ParentPreferences from "@/pages/parent/Preferences";
import ParentInbox from "@/pages/parent/Inbox";
import TeacherPreferences from "@/pages/teacher/Preferences";
import ParentDocuments from "@/pages/parent/Documents";
import ParentAbsences from "@/pages/parent/Absences";
import ParentPickup from "@/pages/parent/Pickup";
import ParentMedications from "@/pages/parent/Medications";
import ParentEvents from "@/pages/parent/Events";
import ParentAutopaySuccess from "@/pages/parent/AutopaySuccess";
import ParentPaymentHistory from "@/pages/parent/PaymentHistory";
import { GlobalSearchProvider } from "@/components/GlobalSearchPalette";
import AdminBilling from "@/pages/admin/Billing";
import ProductBilling from "@/pages/admin/ProductBilling";
import AdminBillingFamily from "@/pages/admin/BillingFamily";
import AdminSubsidyBilling from "@/pages/admin/SubsidyBilling";
import AdminSubsidyClaimWorkspace from "@/pages/admin/SubsidyClaimWorkspace";
import AdminTuitionPlans from "@/pages/admin/TuitionPlans";
import AdminBillingLedger from "@/pages/admin/BillingLedger";
import AdminFamilies from "@/pages/admin/Families";
import AdminChildren from "@/pages/admin/Children";
import AdminClassrooms from "@/pages/admin/Classrooms";
import AdminRatioCapacity from "@/pages/admin/RatioCapacity";
import AdminFoodProgram from "@/pages/admin/FoodProgram";
import AdminStaff from "@/pages/admin/Staff";
import AdminOrganizations from "@/pages/admin/Organizations";
import AdminSettings from "@/pages/admin/Settings";
import AdminAttendance from "@/pages/admin/Attendance";
import AdminDocuments from "@/pages/admin/Documents";
import AdminEvents from "@/pages/admin/Events";
import AdminScheduling from "@/pages/admin/Scheduling";
import AdminTimeOff from "@/pages/admin/TimeOff";
import AdminLearning from "@/pages/admin/Learning";
import AdminInquiries from "@/pages/admin/Inquiries";
import AdminInquiryDetail from "@/pages/admin/InquiryDetail";
import AdminPhotoModeration from "@/pages/admin/PhotoModeration";
import AdminSystem from "@/pages/admin/System";
import AdminLaunchRehearsal from "@/pages/admin/LaunchRehearsal";
import AdminTeacherInstalls from "@/pages/admin/TeacherInstalls";
import AdminConferences from "@/pages/admin/Conferences";
import AdminCompliance from "@/pages/admin/Compliance";
import AdminAnalytics from "@/pages/admin/Analytics";
import TeacherConferences from "@/pages/teacher/Conferences";
import TeacherCompliance from "@/pages/teacher/Compliance";
import ParentConferences from "@/pages/parent/Conferences";
import ParentHealthRecords from "@/pages/parent/HealthRecords";
import SiteLayout from "@/pages/marketing/SiteLayout";
import SiteHome from "@/pages/marketing/Home";
import SiteCmsPage from "@/pages/marketing/CmsPage";
import SiteAnnouncements from "@/pages/marketing/Announcements";
import SiteContact from "@/pages/marketing/Contact";
import { PublicSiteProvider } from "@/context/PublicSiteContext";
import PlatformControlCenter from "@/pages/platform/ControlCenter";
import PlatformBootstrap from "@/pages/platform/Bootstrap";

import { ROLES } from "@/lib/roles";

function App() {
  const module = (moduleKey, element) => (
    <ModuleGate moduleKey={moduleKey}>{element}</ModuleGate>
  );

  return (
    <ErrorBoundary scope="root">
      <AuthProvider>
        <BrandingProvider>
          <OrganizationConfigProvider>
          <BrowserRouter>
            <GlobalSearchProvider>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Public marketing site — multi-tenant at /site/:orgSlug */}
          <Route
            path="/site/:orgSlug"
            element={
              <PublicSiteProvider>
                <SiteLayout />
              </PublicSiteProvider>
            }
          >
            <Route index element={<SiteHome />} />
            <Route path="announcements" element={<SiteAnnouncements />} />
            <Route path="contact" element={<SiteContact />} />
            <Route path="page/:pageSlug" element={<SiteCmsPage />} />
          </Route>

          {/* Default: send to role-specific home */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <RoleRedirect />
              </ProtectedRoute>
            }
          />

          {/* Parent (mobile shell) */}
          <Route
            path="/parent"
            element={
              <ProtectedRoute allow={[ROLES.PARENT]}>
                <MobileShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<ParentDashboard />} />
            <Route path="activity" element={module("daily_activity", <ParentActivity />)} />
            <Route path="family" element={<ParentFamily />} />
            <Route path="activity/:date" element={module("daily_activity", <ParentActivityDetail />)} />
            <Route
              path="incidents"
              element={module("incidents", <IncidentsList basePath="/parent/incidents" />)}
            />
            <Route
              path="incidents/:id"
              element={module("incidents", <IncidentDetail basePath="/parent/incidents" />)}
            />
            <Route path="enrollment" element={module("enrollment", <ParentEnrollment />)} />
            <Route path="enrollment/new" element={module("enrollment", <ParentEnrollmentForm />)} />
            <Route path="enrollment/:id" element={module("enrollment", <ParentEnrollmentStatus />)} />
            <Route path="billing" element={module("family_billing", <ParentBilling />)} />
            <Route path="billing/history" element={module("family_billing", <ParentPaymentHistory />)} />
            <Route path="billing/success" element={module("family_billing", <ParentBillingSuccess />)} />
            <Route path="preferences" element={<ParentPreferences />} />
            <Route path="inbox" element={<ParentInbox />} />
            <Route path="documents" element={module("documents", <ParentDocuments />)} />
            <Route path="absences" element={module("attendance", <ParentAbsences />)} />
            <Route path="pickup" element={module("health_records", <ParentPickup />)} />
            <Route path="medications" element={module("health_records", <ParentMedications />)} />
            <Route path="events" element={module("events", <ParentEvents />)} />
            <Route path="conferences" element={module("conferences", <ParentConferences />)} />
            <Route path="health-records" element={module("health_records", <ParentHealthRecords />)} />
            <Route path="billing/autopay/success" element={module("family_billing", <ParentAutopaySuccess />)} />
            <Route
              path="messages"
              element={module("messaging", <MessagingInbox basePath="/parent/messages" />)}
            />
            <Route
              path="messages/:conversationId"
              element={module("messaging", <MessagingThread basePath="/parent/messages" />)}
            />
            <Route path="profile" element={<ParentFamily />} />
          </Route>

          {/* Teacher (mobile/tablet shell) */}
          <Route
            path="/teacher"
            element={
              <ProtectedRoute allow={[ROLES.TEACHER]}>
                <MobileShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<TeacherDashboard />} />
            <Route path="roster" element={<TeacherRoster />} />
            <Route path="attendance" element={module("attendance", <TeacherAttendance />)} />
            <Route path="reports/:childId" element={module("daily_activity", <TeacherReportEditor />)} />
            <Route path="photos" element={module("photos", <TeacherPhotos />)} />
            <Route path="timesheet" element={module("staffing", <TeacherTimesheet />)} />
            <Route path="schedule" element={module("staffing", <TeacherSchedule />)} />
            <Route path="ratio-capacity" element={module("ratio_capacity", <TeacherRatioCapacity />)} />
            <Route path="food-program" element={module("food_program", <TeacherFoodProgram />)} />
            <Route path="conferences" element={module("conferences", <TeacherConferences />)} />
            <Route path="compliance" element={<TeacherCompliance />} />
            <Route path="preferences" element={<TeacherPreferences />} />
            <Route
              path="incidents"
              element={module("incidents", <IncidentsList basePath="/teacher/incidents" />)}
            />
            <Route path="incidents/new" element={module("incidents", <TeacherIncidentNew />)} />
            <Route
              path="incidents/:id"
              element={module("incidents", <IncidentDetail basePath="/teacher/incidents" />)}
            />
            <Route
              path="messages"
              element={module("messaging", <MessagingInbox basePath="/teacher/messages" />)}
            />
            <Route
              path="messages/:conversationId"
              element={module("messaging", <MessagingThread basePath="/teacher/messages" />)}
            />
          </Route>

          {/* Admin (desktop shell) */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allow={[ROLES.ADMIN]}>
                <DesktopShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="daily-reports" element={module("daily_activity", <AdminDailyReports />)} />
            <Route path="incidents" element={module("incidents", <AdminIncidents />)} />
            <Route path="incidents/new" element={module("incidents", <TeacherIncidentNew />)} />
            <Route
              path="incidents/:id"
              element={module("incidents", <IncidentDetail basePath="/admin/incidents" />)}
            />
            <Route path="enrollment" element={module("enrollment", <AdminEnrollment />)} />
            <Route path="enrollment/:id" element={module("enrollment", <AdminEnrollmentDetail />)} />
            <Route path="admissions" element={module("enrollment", <AdminAdmissions />)} />
            <Route path="billing" element={module("family_billing", <AdminBilling />)} />
            <Route path="billing/ledger" element={module("family_billing", <AdminBillingLedger />)} />
            <Route path="billing/plans" element={module("family_billing", <AdminTuitionPlans />)} />
            <Route path="billing/subsidies" element={module("family_billing", <AdminSubsidyBilling />)} />
            <Route path="billing/subsidies/:claimId" element={module("family_billing", <AdminSubsidyClaimWorkspace />)} />
            <Route path="billing/pricing" element={<ProductBilling />} />
            <Route path="billing/:familyId" element={module("family_billing", <AdminBillingFamily />)} />
            <Route
              path="messaging"
              element={module("messaging", <MessagingInbox basePath="/admin/messaging" />)}
            />
            <Route
              path="messaging/:conversationId"
              element={module("messaging", <MessagingThread basePath="/admin/messaging" />)}
            />
            <Route
              path="organizations"
              element={<AdminOrganizations />}
            />
            <Route path="families" element={<AdminFamilies />} />
            <Route path="families/:familyId" element={<AdminFamilies />} />
            <Route path="children" element={<AdminChildren />} />
            <Route path="classrooms" element={<AdminClassrooms />} />
            <Route path="ratio-capacity" element={module("ratio_capacity", <AdminRatioCapacity />)} />
            <Route path="food-program" element={module("food_program", <AdminFoodProgram />)} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="attendance" element={module("attendance", <AdminAttendance />)} />
            <Route path="documents" element={module("documents", <AdminDocuments />)} />
            <Route path="events" element={module("events", <AdminEvents />)} />
            <Route path="inquiries" element={module("enrollment", <AdminInquiries />)} />
            <Route path="inquiries/:id" element={module("enrollment", <AdminInquiryDetail />)} />
            <Route path="photos/moderation" element={module("photos", <AdminPhotoModeration />)} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="launch-rehearsal" element={<AdminLaunchRehearsal />} />
            <Route path="families/:familyId/activation" element={<AdminFamilies activationDeepLink />} />
            <Route path="teacher-installs" element={<AdminTeacherInstalls />} />
            <Route path="scheduling" element={module("staffing", <AdminScheduling />)} />
            <Route path="time-off" element={module("staffing", <AdminTimeOff />)} />
            <Route path="learning" element={module("learning", <AdminLearning />)} />
            <Route path="conferences" element={module("conferences", <AdminConferences />)} />
            <Route path="compliance" element={<AdminCompliance />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* Platform control (desktop-first, no operational family-data routes) */}
          <Route
            path="/platform/bootstrap"
            element={
              <ProtectedRoute>
                <PlatformBootstrap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform"
            element={
              <ProtectedRoute allow={[ROLES.PLATFORM_ADMIN]}>
                <PlatformShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlatformControlCenter />} />
          </Route>

          {/* CMS Editor (desktop shell) */}
          <Route
            path="/cms"
            element={
              <ProtectedRoute allow={[ROLES.CMS_EDITOR]}>
                <DesktopShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<CmsDashboard />} />
            <Route path="pages" element={<CmsPages />} />
            <Route path="pages/new" element={<CmsPageEditor />} />
            <Route path="pages/:id" element={<CmsPageEditor />} />
            <Route path="announcements" element={<CmsAnnouncements />} />
            <Route path="media" element={<CmsMedia />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
            </GlobalSearchProvider>
          </BrowserRouter>
          </OrganizationConfigProvider>
          <Toaster position="top-right" richColors closeButton />
        </BrandingProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
